import { useCallback, useEffect, useState } from 'react';
import { Mail, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { LinkifiedText } from '@/components/LinkifiedText';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Lisbon' }) : 'Agora';

function WelcomeActions({ onDecide, busy }) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 mt-4">
      <Button disabled={busy} onClick={() => onDecide(true)} className="bg-ruby hover:bg-ruby-light text-white rounded-full" data-testid="welcome-accept">Sim, quero receber mensagens</Button>
      <Button disabled={busy} variant="outline" onClick={() => onDecide(false)} className="rounded-full border-border" data-testid="welcome-decline">Não, prefiro ocultar</Button>
    </div>
  );
}

function MessageRow({ m, open, onToggle, onDecide, busy }) {
  const preview = m.body.split('\n')[0].slice(0, 90);
  return (
    <div className={`rounded-2xl border ${m.read ? 'border-border bg-surface' : 'border-ruby/40 bg-ruby/5'}`} data-testid={`message-${m.id}`}>
      <button className="w-full flex items-start gap-3 p-4 text-left" onClick={onToggle} data-testid={`message-open-${m.id}`}>
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${m.read ? 'bg-transparent' : 'bg-ruby'}`} data-testid={`message-unread-dot-${m.id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm ${m.read ? 'text-muted-foreground' : 'text-foreground font-semibold'}`}>Intimate.pt</span>
            <span className="text-xs text-muted-foreground shrink-0">{fmt(m.sent_at)}</span>
          </div>
          {!open && <p className="text-sm text-muted-foreground truncate mt-0.5">{preview}</p>}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-9" data-testid={`message-body-${m.id}`}>
          <LinkifiedText text={m.body} className="text-sm text-foreground leading-relaxed" />
          {m.kind === 'welcome' && <WelcomeActions onDecide={onDecide} busy={busy} />}
        </div>
      )}
    </div>
  );
}

export default function Mensagens() {
  const { refreshIm } = useAuth();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/im/messages?page=${p}`);
      setItems(prev => (p === 1 ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more);
      setPage(p);
    } catch {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1); }, [load]);

  const toggle = async (m) => {
    setOpenId(openId === m.id ? null : m.id);
    if (!m.read && m.kind !== 'welcome') {
      try {
        await api.post(`/im/messages/${m.id}/read`);
        setItems(prev => prev.map(x => (x.id === m.id ? { ...x, read: true } : x)));
        refreshIm();
      } catch { /* stays unread; retried on next open */ }
    }
  };

  const decide = async (granted) => {
    setBusy(true);
    try {
      await api.post('/im/consent', { granted, source: 'welcome' });
      toast.success(granted ? 'Combinado! Vais receber as novidades aqui.' : 'Preferência guardada. Podes mudar em A minha conta.');
      setOpenId(null);
      await load(1);
      refreshIm();
    } catch {
      toast.error('Erro ao guardar a preferência');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-24" data-testid="messages-page">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-ruby" />
          <h1 className="text-3xl font-bold text-foreground">Mensagens</h1>
        </div>
        <div className="space-y-3">
          {items.map(m => (
            <MessageRow key={m.id} m={m} open={openId === m.id} onToggle={() => toggle(m)} onDecide={decide} busy={busy} />
          ))}
          {!loading && items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-16" data-testid="messages-empty">Ainda não tens mensagens.</p>
          )}
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-ruby" /></div>}
          {hasMore && !loading && (
            <Button variant="outline" className="w-full border-border" onClick={() => load(page + 1)} data-testid="messages-load-more">Ver mensagens anteriores</Button>
          )}
        </div>
      </main>
    </div>
  );
}
